import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAllAttendanceRecords,
  useManagerEmployees,
} from '@saiens/shared/hooks';
import { formatWorkHours } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';

const PAGE_SIZE = 20;

function formatAttendanceTime(dt: string | null): string {
  if (!dt) return '--:--';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return '--:--';
  return d.toLocaleTimeString('zh-TW', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatAttendanceDate(dt: string | null): string {
  if (!dt) return '—';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('zh-TW', {
    month: '2-digit',
    day: '2-digit',
  });
}

export function AllAttendanceRecordsPage() {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [employeeId, setEmployeeId] = useState<number | undefined>();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [offset, setOffset] = useState(0);

  const { data: employees } = useManagerEmployees();
  const { data, isLoading } = useAllAttendanceRecords({
    offset,
    limit: PAGE_SIZE,
    employee_id: employeeId,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  const handleReset = () => {
    setEmployeeId(undefined);
    setDateFrom('');
    setDateTo('');
    setOffset(0);
  };

  return (
    <div className="mx-auto max-w-lg">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="rounded-full p-1 hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">所有打卡紀錄</h1>
        </div>
        <button
          onClick={() => setShowFilters((v) => !v)}
          className="flex items-center gap-1 text-sm text-indigo-600"
        >
          篩選
          {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="space-y-3 border-b bg-gray-50 px-4 py-3">
          <div>
            <label className="mb-1 block text-xs text-gray-500">員工</label>
            <select
              value={employeeId || ''}
              onChange={(e) => { setEmployeeId(e.target.value ? Number(e.target.value) : undefined); setOffset(0); }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">全部員工</option>
              {employees?.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="mb-1 block text-xs text-gray-500">起始日</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs text-gray-500">結束日</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setOffset(0); }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <button
            onClick={handleReset}
            className="w-full rounded-lg border border-gray-300 py-2 text-sm text-gray-600"
          >
            重置篩選
          </button>
        </div>
      )}

      {/* List */}
      <div className="px-4 py-4">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-gray-400">
            無打卡紀錄
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {data.items.map((r) => (
                <div
                  key={r.id}
                  className="rounded-xl bg-white p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">{r.employee_name}</span>
                    <span className="text-xs text-gray-400">
                      {formatAttendanceDate(r.check_in)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">{r.department_name}</p>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                      <span className="text-gray-600">
                        上班 <span className="font-medium text-green-600">{formatAttendanceTime(r.check_in)}</span>
                      </span>
                      <span className="text-gray-600">
                        下班 <span className="font-medium text-orange-600">{formatAttendanceTime(r.check_out)}</span>
                      </span>
                    </div>
                    <span className="text-xs font-semibold text-teal-600">
                      {formatWorkHours(r.worked_hours)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {data.total > PAGE_SIZE && (
              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset === 0}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
                >
                  上一頁
                </button>
                <span className="text-xs text-gray-400">
                  {offset + 1}-{Math.min(offset + PAGE_SIZE, data.total)} / {data.total}
                </span>
                <button
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={offset + PAGE_SIZE >= data.total}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
                >
                  下一頁
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
